import { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { useSectionsStore } from './store'

export function AudioButton() {
  const { isGloballyMuted, toggleGlobalMute } = useSectionsStore()
  const [isHovered, setIsHovered] = useState(false)
  const canvasRef = useRef(null)
  const animationRef = useRef(null)
  const progressRef = useRef(0)
  const yMultiplierRef = useRef(isGloballyMuted ? 0 : 1)
  const timeRef = useRef(0) // For cycling animation

  const width = 24
  const height = 16

  // Initialize canvas and start animation
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const context = canvas.getContext('2d')
    const dpr = 2

    // Set canvas size
    canvas.width = width * dpr
    canvas.height = height * dpr
    canvas.style.width = `${width}px`
    canvas.style.height = `${height}px`
    context.scale(dpr, dpr)

    // Set drawing properties
    context.lineWidth = 1.5
    context.strokeStyle = '#64ffda'

    // Animation function with cycling effect
    const animate = () => {
      timeRef.current += 0.05 // Increment time for cycling effect
      
      const canvasWidth = width + 2
      const canvasHeight = height / 2
      const progress = canvasWidth * progressRef.current
      const baseIncrease = 90 / 180 * Math.PI / (canvasHeight / 2)

      context.clearRect(0, 0, canvas.width, canvas.height)
      context.beginPath()

      let counter = 0
      let x = 0
      let y = canvasHeight

      for (let i = -4; i < canvasWidth; i++) {
        if (progress >= i) {
          context.moveTo(x, y)

          x = i
          
          // Add cycling animation when not muted
          if (!isGloballyMuted && yMultiplierRef.current > 0) {
            // Create multiple sine waves with different frequencies and phases for rich animation
            const wave1 = Math.sin(counter + timeRef.current * 2) * 0.4
            const wave2 = Math.sin(counter * 1.5 + timeRef.current * 1.5) * 0.3
            const wave3 = Math.sin(counter * 0.8 + timeRef.current * 2.5) * 0.2
            const wave4 = Math.sin(counter * 2.2 + timeRef.current * 1.8) * 0.1
            
            // Combine waves for complex cycling pattern
            const combinedWave = wave1 + wave2 + wave3 + wave4
            
            y = canvasHeight - combinedWave * (canvasHeight - 1) * yMultiplierRef.current
          } else {
            // Static flat line when muted
            y = canvasHeight - Math.sin(counter) * (canvasHeight - 1) * yMultiplierRef.current
          }
          
          counter += baseIncrease

          context.lineTo(x, y)
        }
      }

      context.stroke()
      animationRef.current = requestAnimationFrame(animate)
    }

    // Start animation
    animate()

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [isGloballyMuted]) // Re-run when mute state changes

  // Animate in on mount
  useEffect(() => {
    let startTime = Date.now()
    const duration = 1000

    const animateIn = () => {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / duration, 1)
      
      progressRef.current = easeOutExpo(progress)
      
      if (progress < 1) {
        requestAnimationFrame(animateIn)
      }
    }

    animateIn()
  }, [])

  // Update yMultiplier when mute state changes
  useEffect(() => {
    const targetY = isGloballyMuted ? 0 : (isHovered ? 0.8 : 1)
    animateYMultiplier(targetY, 300)
  }, [isGloballyMuted, isHovered])

  // Easing function
  const easeOutExpo = (t) => {
    return t === 1 ? 1 : 1 - Math.pow(2, -10 * t)
  }

  const easeInOutCubic = (t) => {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
  }

  // Animate yMultiplier
  const animateYMultiplier = (target, duration) => {
    const start = yMultiplierRef.current
    const startTime = Date.now()

    const animate = () => {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / duration, 1)
      
      yMultiplierRef.current = start + (target - start) * easeInOutCubic(progress)
      
      if (progress < 1) {
        requestAnimationFrame(animate)
      }
    }

    animate()
  }

  const handleClick = () => {
    toggleGlobalMute()
  }

  const handleMouseEnter = () => {
    setIsHovered(true)
  }

  const handleMouseLeave = () => {
    setIsHovered(false)
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4, ease: "easeOutCubic" }}
      style={{
        position: 'relative',
        width: width + 20,
        height: height + 20,
        cursor: 'pointer',
        pointerEvents: 'auto',
        userSelect: 'none'
      }}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          marginLeft: -width / 2,
          marginTop: -height / 2,
          // Add subtle glow effect when audio is active
          filter: !isGloballyMuted ? 'drop-shadow(0 0 4px rgba(100, 255, 218, 0.3))' : 'none'
        }}
      />
      
      {/* Tooltip */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ 
          opacity: isHovered ? 1 : 0,
          y: isHovered ? 0 : 10
        }}
        transition={{ duration: 0.2 }}
        style={{
          position: 'absolute',
          bottom: '100%',
          right: '-40%',
          transform: 'translateX(-50%)',
          marginBottom: '8px',
          padding: '4px 8px',
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          color: '#64ffda',
          fontSize: '12px',
          borderRadius: '4px',
          whiteSpace: 'nowrap',
          pointerEvents: 'none'
        }}
      >
        {isGloballyMuted ? 'Unmute Audio' : 'Mute Audio'}
      </motion.div>
    </motion.div>
  )
} 